import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Car, Lock, User as UserIcon, AlertCircle, Eye, EyeOff, Loader2, Mail, Building2, UserPlus } from "lucide-react";
import axios from "axios";
import { API_URL } from "../utils/api-config";
import { useAuth } from "../context/AuthContext";
import Button from "../components/ui/Button";
import Input from "../components/ui/Input";
import { Card, CardContent } from "../components/ui/Card";
import Alert from "../components/ui/Alert";

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login: authLogin } = useAuth();
  const [isRegisterMode, setIsRegisterMode] = useState(false);
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    email: "",
    firstName: "",
    lastName: "",
    organizationName: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [focused, setFocused] = useState<string | null>(null);

  // Auto-focus sur le premier champ
  useEffect(() => {
    const timer = setTimeout(() => {
      const usernameInput = document.querySelector('input[name="username"]') as HTMLInputElement;
      if (usernameInput) usernameInput.focus();
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError("");
    setSuccess("");
  };

  const toggleMode = () => {
    setIsRegisterMode(!isRegisterMode);
    setError("");
    setSuccess("");
    setFormData({
      username: "",
      password: "",
      email: "",
      firstName: "",
      lastName: "",
      organizationName: "",
    });
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.username.trim() || !formData.email.trim() || !formData.password.trim()) {
      setError("Username, email et mot de passe sont requis");
      return;
    }

    if (!formData.organizationName.trim()) {
      setError("Le nom de l'organisation est requis");
      return;
    }

    if (formData.password.length < 6) {
      setError("Le mot de passe doit contenir au moins 6 caractères");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await axios.post(`${API_URL}/auth/setup`, {
        username: formData.username,
        email: formData.email,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName,
        organizationName: formData.organizationName,
      });

      if (response.data.success) {
        setSuccess("Compte Super Admin créé avec succès ! Vous pouvez maintenant vous connecter.");
        setIsRegisterMode(false);
        setFormData({
          ...formData,
          password: "",
          email: "",
          firstName: "",
          lastName: "",
          organizationName: "",
        });
      }
    } catch (err: any) {
      console.error("Erreur d'inscription:", err);
      setError(err.response?.data?.message || "Erreur lors de la création du compte");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isRegisterMode) {
      return handleRegister(e);
    }

    if (!formData.username.trim()) {
      setError("Le nom d'utilisateur est requis");
      return;
    }
    if (!formData.password.trim()) {
      setError("Le mot de passe est requis");
      return;
    }

    setLoading(true);
    setError("");

    try {
      await authLogin(formData.username, formData.password);
      const from = (location.state as any)?.from || "/";
      navigate(from, { replace: true });
    } catch (err: any) {
      console.error("Erreur de connexion:", err);
      setError(
        err.response?.data?.message || "Erreur de connexion. Vérifiez vos identifiants."
      );
    } finally {
      setLoading(false);
    }
  };

  // Connexion rapide avec identifiants pré-remplis
  const handleQuickLogin = async (username: string, password: string) => {
    setLoading(true);
    setError("");
    setFormData({ username, password });

    try {
      await authLogin(username, password);
      const from = (location.state as any)?.from || "/";
      navigate(from, { replace: true });
    } catch (err: any) {
      console.error("Erreur de connexion:", err);
      setError(
        err.response?.data?.message || "Erreur de connexion. Vérifiez vos identifiants."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-blue-50 via-white to-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md" style={{ animation: 'fadeIn 0.5s ease-in-out' }}>
        {/* Header avec logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-24 h-24 bg-white rounded-2xl mb-4 shadow-lg transform transition-transform hover:scale-105 p-3">
            <img src="/logo.png" alt="Logo" className="w-full h-full object-contain" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2" style={{ fontFamily: '"Bauhaus 93", "Orbitron", sans-serif' }}>
            Master<span style={{ color: '#64B5F6' }}>D</span>i Fleet
          </h1>
          <p className="text-gray-600">
            Connectez-vous pour accéder à votre espace
          </p>
        </div>

        {/* Carte de connexion/inscription */}
        <Card className="shadow-2xl border-0">
          <CardContent className="p-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6 text-center">
              {isRegisterMode ? "Créer un compte Super Admin" : "Connexion"}
            </h2>

            {/* Message d'erreur */}
            {error && (
              <div className="mb-4" style={{ animation: 'slideDown 0.3s ease-out' }}>
                <Alert severity="error">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <span>{error}</span>
                  </div>
                </Alert>
              </div>
            )}

            {/* Message de succès */}
            {success && (
              <div className="mb-4" style={{ animation: 'slideDown 0.3s ease-out' }}>
                <Alert severity="success">
                  <span>{success}</span>
                </Alert>
              </div>
            )}

            {/* Formulaire */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Champs d'inscription */}
              {isRegisterMode && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      label="Prénom"
                      name="firstName"
                      type="text"
                      value={formData.firstName}
                      onChange={handleChange}
                      fullWidth
                      placeholder="Prénom"
                      disabled={loading}
                    />
                    <Input
                      label="Nom"
                      name="lastName"
                      type="text"
                      value={formData.lastName}
                      onChange={handleChange}
                      fullWidth
                      placeholder="Nom"
                      disabled={loading}
                    />
                  </div>
                  <Input
                    label="Email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    fullWidth
                    startAdornment={<Mail className="w-4 h-4 text-gray-400" />}
                    placeholder="votre@email.com"
                    disabled={loading}
                  />
                  <Input
                    label="Nom de l'organisation"
                    name="organizationName"
                    type="text"
                    value={formData.organizationName}
                    onChange={handleChange}
                    required
                    fullWidth
                    startAdornment={<Building2 className="w-4 h-4 text-gray-400" />}
                    placeholder="Ma Société"
                    disabled={loading}
                  />
                </>
              )}

              {/* Nom d'utilisateur */}
              <div>
                <Input
                  label="Nom d'utilisateur"
                  name="username"
                  type="text"
                  value={formData.username}
                  onChange={handleChange}
                  onFocus={() => setFocused("username")}
                  onBlur={() => setFocused(null)}
                  required
                  fullWidth
                  startAdornment={
                    <UserIcon 
                      className={`w-4 h-4 transition-colors ${
                        focused === "username" ? "text-primary" : "text-gray-400"
                      }`} 
                    />
                  }
                  placeholder="Entrez votre nom d'utilisateur"
                  autoComplete="username"
                  disabled={loading}
                />
              </div>

              {/* Mot de passe */}
              <div>
                <Input
                  label="Mot de passe"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={handleChange}
                  onFocus={() => setFocused("password")}
                  onBlur={() => setFocused(null)}
                  required
                  fullWidth
                  startAdornment={
                    <Lock 
                      className={`w-4 h-4 transition-colors ${
                        focused === "password" ? "text-primary" : "text-gray-400"
                      }`} 
                    />
                  }
                  endAdornment={
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                      tabIndex={-1}
                    >
                      {showPassword ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </button>
                  }
                  placeholder={isRegisterMode ? "Minimum 6 caractères" : "Entrez votre mot de passe"}
                  autoComplete={isRegisterMode ? "new-password" : "current-password"}
                  disabled={loading}
                />
              </div>

              {/* Se souvenir de moi (connexion seulement) */}
              {!isRegisterMode && (
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 cursor-pointer group">
                    <input
                      type="checkbox"
                      className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-2 focus:ring-primary focus:ring-offset-0 cursor-pointer transition-all"
                      disabled={loading}
                    />
                    <span className="text-sm text-gray-600 group-hover:text-gray-900 transition-colors">
                      Se souvenir de moi
                    </span>
                  </label>
                  <button
                    type="button"
                    className="text-sm text-primary hover:text-primary-600 hover:underline transition-all font-medium"
                    onClick={() => {}}
                    disabled={loading}
                  >
                    Mot de passe oublié ?
                  </button>
                </div>
              )}

              {/* Bouton principal */}
              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={loading || !formData.username.trim() || !formData.password.trim() || (isRegisterMode && (!formData.email.trim() || !formData.organizationName.trim()))}
                className="py-3 mt-4 shadow-lg hover:shadow-xl transition-all duration-200"
                startIcon={loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (isRegisterMode ? <UserPlus className="w-4 h-4" /> : undefined)}
              >
                {loading 
                  ? (isRegisterMode ? "Création en cours..." : "Connexion en cours...") 
                  : (isRegisterMode ? "Créer le compte" : "Se connecter")
                }
              </Button>

              {/* Toggle inscription/connexion */}
              <div className="text-center pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={toggleMode}
                  className="text-sm text-primary hover:text-primary-600 hover:underline transition-all font-medium"
                  disabled={loading}
                >
                  {isRegisterMode 
                    ? "Déjà un compte ? Se connecter" 
                    : "Créer un compte Super Admin"
                  }
                </button>
              </div>
            </form>

            {/* Connexion rapide (masqué en mode inscription) */}
            {!isRegisterMode && (
              <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200/50">
                <p className="text-xs font-semibold text-blue-900 mb-3 flex items-center gap-1">
                  <span>⚡</span>
                  <span>Connexion rapide (développement) :</span>
                </p>
                
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <button
                    type="button"
                    onClick={() => handleQuickLogin("superadmin", "SuperAdmin123!")}
                    disabled={loading}
                    className="px-4 py-2 bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white text-sm font-medium rounded-lg transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    🔐 Super Admin
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => handleQuickLogin("manager", "Manager123!")}
                    disabled={loading}
                    className="px-4 py-2 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white text-sm font-medium rounded-lg transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    👨‍💼 Manager
                  </button>
                </div>

                <div className="space-y-1 text-xs text-blue-800 pt-2 border-t border-blue-200">
                  <p>
                    • Super Admin: <code className="bg-blue-100 px-1.5 py-0.5 rounded font-mono">superadmin</code>
                  </p>
                  <p>
                    • Manager: <code className="bg-blue-100 px-1.5 py-0.5 rounded font-mono">manager</code>
                  </p>
                </div>
                <p className="text-xs text-blue-600 mt-2 flex items-center gap-1">
                  <span>⚠️</span>
                  <span>Changez les mots de passe en production</span>
                </p>
              </div>
            )}

            {/* Info inscription */}
            {isRegisterMode && (
              <div className="mt-6 p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg border border-purple-200/50">
                <p className="text-xs text-purple-800">
                  <strong>Note :</strong> Ce formulaire crée un compte Super Admin avec tous les droits,
                  ainsi qu'une nouvelle organisation dédiée.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="mt-6 text-center text-sm text-gray-500">
          <p style={{ fontFamily: '"Bauhaus 93", "Orbitron", sans-serif' }}>© 2025 Master<span style={{ color: '#64B5F6' }}>D</span>i Fleet</p>
          <p className="mt-1 text-xs">Version 1.0.0</p>
        </div>
      </div>
    </div>
  );
};

export default Login;

